/**
 * Cookie-session login (Phase 5b) — replaces `AdminGate.tsx`'s shared-
 * password screen. Two-step OTP flow against the backend's real human-
 * auth endpoints (`services/auth/otp.py` via `POST /api/auth/login/
 * {start,verify}`):
 *
 *   1. Email -> `POST /login/start`. Always 202 regardless of whether
 *      the address has an account (the backend's enumeration-
 *      resistance design, `otp.py::start_login` — same response
 *      shape + a timing floor either way) — this screen shows the
 *      SAME "check your email" copy unconditionally, never "no
 *      account with that email", to not undo that work at the UI
 *      layer.
 *   2. 6-digit code -> `POST /login/verify` -> sets the session +
 *      CSRF cookies -> `refresh()`s the auth context so the rest of
 *      the app immediately sees the new identity -> redirect to the
 *      intended path (`?next=`, set by `services/csrf.ts`'s
 *      `redirectToLogin()` when an API call 401s, or by
 *      `RequireSession` when an anonymous visitor hits a gated
 *      route). Defaults to `/fleet`.
 */

import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, KeyRound, Mail } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { extractErrorMessage } from '@/lib/utils';
import api from '@/services/api';
import { useAuthSession } from '@/state/authSession';

type Step = 'email' | 'code';

/** Only ever follow a same-app relative path — `?next=` is attacker-
 *  controllable (anyone can link `/login?next=https://evil`), so this
 *  is an open-redirect guard, not just a default-value convenience. */
function safeNextPath(raw: string | null): string {
  if (raw && raw.startsWith('/') && !raw.startsWith('//')) return raw;
  return '/fleet';
}

export const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refresh } = useAuthSession();
  const nextPath = safeNextPath(searchParams.get('next'));

  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const submitEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) {
      setError('Enter your email address.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.loginStart(trimmed);
      setInfo(`If ${trimmed} has an account, a 6-digit code was just sent to it. Check your inbox.`);
      setStep('code');
    } catch (e) {
      setError(extractErrorMessage(e, 'Could not send the code — try again in a moment.'));
    } finally {
      setBusy(false);
    }
  };

  const submitCode = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = code.trim();
    if (!/^\d{6}$/.test(trimmed)) {
      setError('Enter the 6-digit code from your email.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.loginVerify(email.trim(), trimmed);
      await refresh();
      navigate(nextPath, { replace: true });
    } catch (e) {
      // 401 = wrong/expired/locked code — otp.py never distinguishes
      // "wrong" from "unknown email" here either (same enumeration-
      // resistance posture as /login/start), so this copy stays
      // generic on purpose.
      setError(extractErrorMessage(e, 'Incorrect or expired code.'));
      setCode('');
    } finally {
      setBusy(false);
    }
  };

  const backToEmail = () => {
    setStep('email');
    setCode('');
    setError(null);
    setInfo(null);
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-bg-base px-4">
      <Card className="w-full max-w-sm p-6">
        <div className="mb-4 flex items-center gap-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-accent/15 text-accent">
            T
          </div>
          <div className="font-mono text-lg font-bold tracking-wide text-accent">TuviON</div>
          {step === 'email' ? (
            <Mail className="ml-auto size-4 text-fg-secondary" />
          ) : (
            <KeyRound className="ml-auto size-4 text-fg-secondary" />
          )}
        </div>

        <h1 className="text-lg font-semibold">
          {step === 'email' ? 'Admin login' : 'Enter your code'}
        </h1>

        {step === 'email' && (
          <form className="mt-4 space-y-3" onSubmit={(e) => void submitEmail(e)}>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              autoFocus
              autoComplete="email"
              maxLength={320}
            />
            {error && <p className="text-sm text-status-error">{error}</p>}
            <Button type="submit" disabled={busy} className="w-full">
              {busy ? 'Sending…' : 'Send code'}
            </Button>
          </form>
        )}

        {step === 'code' && (
          <form className="mt-4 space-y-3" onSubmit={(e) => void submitCode(e)}>
            {info && <p className="text-sm text-fg-secondary">{info}</p>}
            <Input
              type="text"
              inputMode="numeric"
              pattern="[0-9]{6}"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))}
              placeholder="000000"
              autoFocus
              autoComplete="one-time-code"
              maxLength={6}
              className="text-center font-mono text-lg tracking-[0.3em]"
            />
            {error && <p className="text-sm text-status-error">{error}</p>}
            <Button type="submit" disabled={busy || code.length !== 6} className="w-full">
              {busy ? 'Verifying…' : 'Verify'}
            </Button>
            <button
              type="button"
              onClick={backToEmail}
              disabled={busy}
              className="flex w-full items-center justify-center gap-1 text-xs text-fg-secondary hover:text-fg"
            >
              <ArrowLeft className="size-3.5" />
              Use a different email
            </button>
          </form>
        )}
      </Card>
    </div>
  );
};

export default LoginPage;
